import { z } from 'zod';
import { TypeOfAPIError, TypeOfAPIResponse } from '@ninja/api-client';
import { APIFactoryOptions, factory } from '../../../config/apiClientConfig';

const policyDetailSchema = z.object({
	policyNumber: z.string(),
	issueDate: z.string(),
	effectiveDate: z.string(),
	expiryDate: z.string(),
});

const memberAnswerSchema = z.object({
	id: z.number(),
	questionId: z.number(),
	isCustomAnswer: z.boolean(),
	answerText: z.string(),
	answerParentId: z.number(),
	customAnswerText: z.string().nullable(),
	answerResourceKey: z.string(),
});

const memberQuestionSchema = z.object({
	id: z.number(),
	questionText: z.string(),
	dataTypeId: z.number(),
	parentQuestionId: z.number().nullable(),
	questionTextKey: z.string(),
	questionResourceKey: z.string(),
	answers: z.array(memberAnswerSchema),
	savedAnswerId: z.number().nullable(),
	savedAnswer: z.string().nullable(),
});

const memberDetailSchema = z.object({
	principalName: z.string(),
	fullName: z.string(),
	nationality: z.string(),
	dateOfBirth: z.string(),
	gender: z.string(),
	weight: z.number(),
	height: z.number(),
	relationship: z.string(),
	emailAddress: z.string(),
	mobileNumber: z.string(),
	isMedicalConditionDeclared: z.boolean(),
	memberId: z.string(),
	mafDocumentUrl: z.string(),
});

const responseSchema = z.object({
	policyInfo: policyDetailSchema,
	memberInfo: memberDetailSchema,
	memberQuestions: z.array(memberQuestionSchema),
});

export const getMemberDetails = (options: APIFactoryOptions) =>
	factory(options).createAPIDefinition({
		url: '/Member/member-details/:encryptedId ',
		method: 'GET',
		requestParameters: { encryptedId: ['required', 'string'] },
		responseSchema: responseSchema,
		errorCodes: ['InvalidPolicyMemberMappingId'],
		transformResponse(response) {
			const { policyDetail, memberDetail } = response.data;

			const {
				principalName,
				fullName,
				nationality,
				dateOfBirth,
				gender,
				weight,
				height,
				relationship,
				emailAddress,
				mobileNumber,
				isMedicalConditionDeclared,
				memberId,
				questions,
				answers,
				savedMedicalAnswers,
				mafDocumentUrl,
			} = memberDetail;

			return {
				policyInfo: policyDetail,
				memberInfo: {
					principalName,
					fullName,
					nationality,
					dateOfBirth,
					gender,
					weight,
					height,
					relationship,
					emailAddress,
					mobileNumber,
					isMedicalConditionDeclared,
					memberId,
					mafDocumentUrl,
				},
				memberQuestions: questions.map((q: { id: number }) => {
					const savedAnswer = savedMedicalAnswers.find((ans: { questionId: number }) => ans.questionId === q.id);

					return {
						...q,
						answers: answers.filter((ans: { questionId: number }) => ans.questionId === q.id),
						savedAnswerId: savedAnswer?.answerId || null,
						savedAnswer: savedAnswer?.answer || null,
					};
				}),
			};
		},
	});

export default getMemberDetails;

export type GetMemberDetails = TypeOfAPIResponse<ReturnType<typeof getMemberDetails>>;

export type GetMemberDetailsErrors = TypeOfAPIError<ReturnType<typeof getMemberDetails>>;
